import { useSession } from '@zhiyue/core';
import { Button, Result, Spin } from 'antd';
import React from 'react';
import { readCurrentTenantUuid } from '@/adapters/tenantStorage';
import { getEnterpriseBootstrap } from '@/services/bff/enterprise.api';
import type {
  EnterpriseBootstrapResponse,
  EnterpriseBootstrapState,
} from '@/services/bff/types/onboarding';

const VALID_STATES: EnterpriseBootstrapState[] = [
  'PROFILE_REQUIRED',
  'PARK_REQUIRED',
  'ADMISSION_PENDING',
  'ACTIVE',
  'SUSPENDED_ONLY',
];

type BootstrapSnapshot = {
  tenantUuid?: string;
  requestId: number;
  data?: EnterpriseBootstrapResponse;
  loading: boolean;
  error?: unknown;
};

const emptySnapshot: BootstrapSnapshot = { requestId: 0, loading: true };
let snapshot = emptySnapshot;
let requestSequence = 0;
const listeners = new Set<() => void>();
let activeRequest: {
  key: string;
  promise: Promise<EnterpriseBootstrapResponse>;
} | null = null;

const publish = (next: BootstrapSnapshot) => {
  snapshot = next;
  listeners.forEach((listener) => {
    listener();
  });
};

export const shouldApplyBootstrapResult = (
  requestId: number,
  currentRequestId: number,
  requestTenantUuid: string,
  currentTenantUuid: string,
) => requestId === currentRequestId && requestTenantUuid === currentTenantUuid;

export const invalidateEnterpriseBootstrap = (tenantUuid?: string) => {
  requestSequence += 1;
  activeRequest = null;
  publish({
    tenantUuid,
    requestId: requestSequence,
    loading: Boolean(tenantUuid),
    error: undefined,
  });
  return requestSequence;
};

export const resetEnterpriseBootstrapForTests = () => {
  requestSequence = 0;
  activeRequest = null;
  snapshot = emptySnapshot;
};

const isBootstrapState = (value: unknown): value is EnterpriseBootstrapState =>
  typeof value === 'string' &&
  VALID_STATES.includes(value as EnterpriseBootstrapState);

const normalizeBootstrapResponse = (response: EnterpriseBootstrapResponse) => {
  if (!isBootstrapState(response.state)) {
    throw new Error('企业启动状态无效');
  }
  return {
    ...response,
    parks: response.parks ?? response.admissionParks ?? [],
  } satisfies EnterpriseBootstrapResponse;
};

const loadBootstrap = async (requestKey: string) => {
  if (activeRequest?.key === requestKey) return activeRequest.promise;
  const promise = getEnterpriseBootstrap()
    .then((response) => normalizeBootstrapResponse(response))
    .finally(() => {
      if (activeRequest?.promise === promise) activeRequest = null;
    });
  activeRequest = { key: requestKey, promise };
  return promise;
};

export const getEnterpriseBootstrapSnapshot = () => snapshot;

export const subscribeEnterpriseBootstrap = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

interface EnterpriseBootstrapContextValue extends BootstrapSnapshot {
  refresh: () => Promise<EnterpriseBootstrapResponse | undefined>;
}

const EnterpriseBootstrapContext = React.createContext<
  EnterpriseBootstrapContextValue | undefined
>(undefined);

export const EnterpriseBootstrapProvider: React.FC<
  React.PropsWithChildren<{ setInitialState: (updater: any) => void }>
> = ({ children, setInitialState }) => {
  const { currentUser, loading: sessionLoading } = useSession();
  const tenantUuid = currentUser?.tenantUuid || readCurrentTenantUuid() || '';
  const requestKey = `${currentUser?.id || ''}:${tenantUuid}`;
  const [localSnapshot, setLocalSnapshot] = React.useState(snapshot);

  React.useEffect(() => {
    const unsubscribe = subscribeEnterpriseBootstrap(() =>
      setLocalSnapshot(getEnterpriseBootstrapSnapshot()),
    );
    return () => unsubscribe();
  }, []);

  const refresh = React.useCallback(async () => {
    if (!currentUser || !tenantUuid) return undefined;
    const requestId = ++requestSequence;
    publish({
      tenantUuid,
      requestId,
      data: snapshot.tenantUuid === tenantUuid ? snapshot.data : undefined,
      loading: true,
      error: undefined,
    });
    try {
      const data = await loadBootstrap(requestKey);
      if (
        shouldApplyBootstrapResult(
          requestId,
          requestSequence,
          tenantUuid,
          currentUser.tenantUuid || readCurrentTenantUuid() || '',
        )
      ) {
        publish({ tenantUuid, requestId, data, loading: false });
        return data;
      }
      return undefined;
    } catch (error) {
      if (
        shouldApplyBootstrapResult(
          requestId,
          requestSequence,
          tenantUuid,
          currentUser.tenantUuid || readCurrentTenantUuid() || '',
        )
      ) {
        publish({ tenantUuid, requestId, loading: false, error });
      }
      return undefined;
    }
  }, [currentUser, requestKey, tenantUuid]);

  React.useEffect(() => {
    if (sessionLoading) {
      if (snapshot.tenantUuid !== tenantUuid || !snapshot.data) {
        publish({
          tenantUuid: tenantUuid || undefined,
          requestId: snapshot.requestId,
          loading: true,
        });
      }
      return;
    }
    if (!currentUser || !tenantUuid) {
      invalidateEnterpriseBootstrap();
      return;
    }
    if (snapshot.tenantUuid !== tenantUuid) {
      invalidateEnterpriseBootstrap(tenantUuid);
    }
    void refresh();
  }, [currentUser, refresh, requestKey, sessionLoading, tenantUuid]);

  React.useEffect(() => {
    if (typeof setInitialState !== 'function') {
      return;
    }

    setInitialState((state: any) =>
      state
        ? {
            ...state,
            enterpriseBootstrap: {
              state: localSnapshot.data?.state,
              loading: localSnapshot.loading,
            },
          }
        : state,
    );
  }, [localSnapshot.data?.state, localSnapshot.loading, setInitialState]);

  const value = React.useMemo(
    () => ({ ...localSnapshot, refresh }),
    [localSnapshot, refresh],
  );

  return (
    <EnterpriseBootstrapContext.Provider value={value}>
      {children}
    </EnterpriseBootstrapContext.Provider>
  );
};

export const useEnterpriseBootstrap = () => {
  const value = React.useContext(EnterpriseBootstrapContext);
  if (!value) {
    throw new Error(
      'useEnterpriseBootstrap must be used within EnterpriseBootstrapProvider',
    );
  }
  return value;
};

export const BootstrapLoading: React.FC = () => (
  <main style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
    <Spin size="large" description="正在加载企业状态" />
  </main>
);

export const BootstrapError: React.FC<{ onRetry: () => void }> = ({
  onRetry,
}) => (
  <main
    style={{
      minHeight: '100vh',
      display: 'grid',
      placeItems: 'center',
      padding: 24,
    }}
  >
    <Result
      status="error"
      title="企业状态加载失败"
      subTitle="请重试，完成加载后才能进入企业端。"
      extra={
        <Button type="primary" onClick={onRetry}>
          重新加载
        </Button>
      }
    />
  </main>
);
